import React, { useState, useEffect, useCallback } from "react";
import cn from "classnames";
import fileSaver from "file-saver";
import { svg2png } from "svg-png-converter";
import Logo from "./components/Logo";
import Export from "./components/Export";
import Character from "./components/Character";
import Footer from "./components/Footer";
import Modal from "./components/Modal";
import Auth from "./components/Auth";
import Message from "./components/Message";
import isSignedIn from "./actions/isSignedIn";

const Sapiens = (props) => {
  const [loading, setLoading] = useState(true);

  const [character, setCharacter] = useState({});
  const [visibleModal, setVisibleModal] = useState(false);
  const [image, setImage] = useState();

  // login
  const [logged, setLogged] = useState(false);
  const [visibleAuth, setVisibleAuth] = useState(false);
  const [welcomeShown, setWelcomeShown] = useState(false);

  const [visibleWelcomeBack, setVisibleWelcomeBack] = useState(false);
  const [visibleWelcome, setVisibleWelcome] = useState(false);

  useEffect(() => {
    isSignedIn(setLogged);
  }, []);

  // Memoize setCharacter to prevent unnecessary re-renders
  const handleCharacterChange = useCallback((newCharacter) => {
    setCharacter(newCharacter);
  }, []);

  const showWelcomeBackMessage = () => {
    setWelcomeShown(true);
    setVisibleWelcomeBack(true);
    setTimeout(() => setVisibleWelcomeBack(false), 4000);
  };

  const setSignIn = (isSignedIn) => {
    setLogged(isSignedIn);
    setVisibleAuth(!isSignedIn);
    if (isSignedIn && !loading) {
      showWelcomeBackMessage();
    }
  };

  const showWelcomeMessage = () => {
    setWelcomeShown(true);
    setVisibleWelcome(true);
    setTimeout(() => setVisibleWelcome(false), 4000);
  };

  const setSignUp = (isSignedIn) => {
    setLogged(isSignedIn);
    setVisibleAuth(!isSignedIn);
    if (isSignedIn && !loading) {
      showWelcomeMessage();
    }
  };

  const setLoadingStatus = (status) => {
    setLoading(status);
    if (logged && !status && !welcomeShown) {
      showWelcomeMessage();
    }
  };

  const getSVG = (url) =>
    fetch(url)
      .then((response) => response.text())
      .then((data) => {
        const parser = new DOMParser();
        return parser.parseFromString(data, "text/xml").querySelector("svg");
      });

  const handleExport = useCallback(
    async (type) => {
      // sizes
      let width = 720;
      let height = 640;
      if (type === "png") {
        width = 1440;
        height = 1280;
      }

      // get svg files
      const svgHead = await getSVG(character.head);
      const svgTop = await getSVG(character.top);
      const svgBottom = await getSVG(character.bottom);
      const svgBackdrop = await getSVG(character.backdropAsset);

      // read & refactor
      const svgRegexId = /id="/g; // fix - rename for same defs id
      const svgRegexUrl = /url\(#/g; // fix - rename for same defs for url
      const svgRegexHref = /xlink:href="#/g; // fix - rename for same defs href
      const svgHeadContent = svgHead.innerHTML
        .replace(svgRegexId, `id="hd-`)
        .replace(svgRegexUrl, `url(#hd-`)
        .replace(svgRegexHref, `xlink:href="#hd-`);
      const svgTopContent = svgTop.innerHTML
        .replace(svgRegexId, `id="tp-`)
        .replace(svgRegexUrl, `url(#tp-`)
        .replace(svgRegexHref, `xlink:href="#tp-`);
      const svgBottomContent = svgBottom.innerHTML
        .replace(svgRegexId, `id="bt-`)
        .replace(svgRegexUrl, `url(#bt-`)
        .replace(svgRegexHref, `xlink:href="#bt-`);
      const svgBackdropContent = svgBackdrop.innerHTML
        .replace(svgRegexId, `id="bd-`)
        .replace(svgRegexUrl, `url(#bd-`)
        .replace(svgRegexHref, `xlink:href="#bd-`);
      const svgHeader = `<svg width="${width}" height="${height}" viewBox="0 0 720 640" fill="black" xmlns="http://www.w3.org/2000/svg">`;
      const svgCharacter = `${svgHeader}${svgBackdropContent}<g transform="translate(120, 80)">${svgHeadContent}${svgBottomContent}${svgTopContent}</g></svg>`;
      const svgData = new Blob([svgCharacter], {
        type: "image/svg+xml; charset=utf-8",
      });

      if (type === "svg") {
        fileSaver.saveAs(svgData, "sapiens.svg");
      }

      if (type === "png") {
        const png = await svg2png({
          input: svgCharacter.trim(),
          encoding: "buffer",
          width: width,
          height: height,
        });
        const pngData = new Blob([png], { type: "image/png" });
        fileSaver.saveAs(pngData, "sapiens.png");
      }

      setImage(svgCharacter);
      setVisibleModal(true);
    },
    [character]
  );

  return (
    <div className={cn("sapiens", { visible: !loading })}>
      <Logo />
      <Export
        logged={logged}
        openAuth={() => setVisibleAuth(true)}
        onExport={handleExport}
      />
      <Character
        setLoading={setLoadingStatus}
        onChange={handleCharacterChange}
        history={props.history}
      />
      <Footer />
      {visibleModal && (
        <Modal
          image={image}
          visibleModal={visibleModal}
          setVisibleModal={setVisibleModal}
        />
      )}
      <Auth
        isOpen={visibleAuth}
        setSignIn={setSignIn}
        setSignUp={setSignUp}
        closeAuth={() => setVisibleAuth(false)}
      />
      <Message visible={visibleWelcomeBack} content="Welcome back!" />
      <Message visible={visibleWelcome} content="Welcome!" />
      <div className="mobile">Mobile support coming soon</div>
    </div>
  );
};

export default Sapiens;
